import React, { useEffect } from 'react';
import { Controller, DeepPartial, UseFormReturn } from 'react-hook-form';
import { noop } from 'lodash/fp';
import { InformationPopUp } from 'components';
import { FormDataType, useFormState, titleList, keyNames, Title } from 'contexts/FormProvider';
import Dependents from '../components/Dependents';
import { useContent, useContentContext } from '@qbe/dfc-content';

import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { Dropdown, FormGroupLabel, TextField, Button } from '@qbe/ads-core';
import { FormSpacer } from 'components';
import { setFormCompleteEvent, setFormStartEvent } from 'hooks/useAnalytics';

interface PersonalDetailsProps {
  form: UseFormReturn<FormDataType>;
  showNextButton: boolean;
  enableNextButton: boolean;
  onSubmit: () => void;
}
const PersonalDetails = ({
  form,
  showNextButton = true,
  enableNextButton = false,
  onSubmit = noop,
}: PersonalDetailsProps) => {
  const {
    control,
    formState: { errors },
    register,
    watch,
    setValue,
    trigger: triggerValidation,
  } = form;
  const { formData, updateForm } = useFormState();

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const policyOrClaim = watch('policyOrClaim') || '';
  const isComplaint = watch('lodgement') === 'Complaint';
  const isOtherLanguage = watch('language') === 'Other';
  const isLangInterpreter = watch('isLangInterpreter');

  const options = [
    {
      text: 'Claim',
      value: 'claim',
    },
    {
      text: 'Policy',
      value: 'policy',
    },
  ];

  React.useEffect(() => {
    if (formData?.policyOrClaim === 'policy' || formData?.claimNumber === '') {
      updateForm({ policyOrClaim: '' });
      setValue('policyOrClaim', ''); // to forcefully reset the field value
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    register('claimNumber');
    register('policyNumber');
  }, [register, policyOrClaim]);

  useEffect(() => {
    register('isLangInterpreter');
    register('language');
    register('vulnerableCustomer');
    register('otherLanguage');
  }, [register]);

  useEffect(() => {
    keyNames.forEach((name) => register(name as keyof FormDataType));
  }, [register]);

  const isDependentValid =
    typeof errors.dependents === 'undefined' || (Array.isArray(errors.dependents) && errors.dependents.length === 0);

  const isValid = enableNextButton && isDependentValid;

  function handleClick(): void {
    setFormCompleteEvent();
    onSubmit();
  }

  // Localized content constants
  const sectionTitle = getTextContent('yourDetails.personalDetails.section');
  const sectionHeading = getTextContent('Financial.personalDetails.sectionHeading');
  const claimNumberTitle = getTextContent('consent.contactReason.claimNumber');
  const claimNumberInfoText = getTextContent('consent.contactReason.claimNumber.infoText');
  const contactInfoComplaint = getTextContent('consent.contactReason.contactInformation');
  const contactInfoFinancial = getTextContent('Financial.contactReason.contactInformation');
  const customerTitleTitle = getTextContent('insured.customerDetails.customerTitle');
  const customerNameTitle = getTextContent('insured.customerDetails.customerName');
  const languageInterpreterTitle = getTextContent('insured.otherDetails.languageInterpreter');
  const languageTitle = getTextContent('insured.otherDetails.language');
  const otherLanguageTitle = getTextContent('insured.otherDetails.otherLanguage');

  return (
    <>
      <FormCard title={sectionTitle} variant="sectionTitle">
        <Controller
          name="policyOrClaim"
          rules={{ required: '' }}
          control={control}
          render={({ field }) => (
            <Toggle
              {...field}
              title={sectionHeading}
              required
              defaultValue={policyOrClaim}
              data-track="policyOrClaim"
              data-track-value="capture"
              optionList={options.map(({ text, value }) => {
                return {
                  autocomplete: 'off',
                  id: value,
                  value,
                  label: text,
                  defaultChecked: value === policyOrClaim,
                };
              })}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target;
                setValue('policyOrClaim', value);
                updateForm({ policyOrClaim: value });
                triggerValidation('policyOrClaim');
                setFormStartEvent();
                field.onChange(value);
              }}
            />
          )}
        />

        {policyOrClaim === 'claim' && (
          <>
            <FormGroupLabel
              alignCenter
              required
              title={claimNumberTitle}
              titleSuffix={<InformationPopUp message={claimNumberInfoText} />}
              subtitle={isComplaint ? contactInfoComplaint : contactInfoFinancial}
            />
            <FormSpacer type="input" />
            <Controller
              name="claimNumber"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!errors.claimNumber}
                  value={field.value as string | undefined}
                  helperText={errors?.claimNumber?.message || null}
                  label="Claim number"
                  id="claimNumber"
                  data-track="claimNumber"
                  data-track-value="capture"
                  inputProps={{
                    maxLength: 50,
                    minLength: 4,
                  }}
                  data-test="claimNumber"
                />
              )}
            />
          </>
        )}

        <FormGroupLabel title={customerTitleTitle} alignCenter />
        <FormSpacer type="input" />
        <Controller
          control={control}
          name="title"
          render={({ field }) => (
            <Dropdown
              {...field}
              id="title"
              label="Title"
              value={field.value as Title}
              multiple={false}
              sx={{ maxWidth: '400px' }}
              data-track="title"
              data-track-value="capture"
              optionList={[...titleList].map((title) => ({
                autocompplete: 'off',
                id: title,
                value: title,
                label: title,
              }))}
            />
          )}
        />

        <FormSpacer type="input" />
        <FormGroupLabel required title={customerNameTitle} alignCenter />
        <FormSpacer type="input" />
        <Controller
          name="firstname"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              error={!!errors.firstname}
              label={'First name'}
              id="firstname"
              data-track="firstname"
              data-track-value="mask"
              inputProps={{
                maxLength: 50,
                minLength: 1,
              }}
              data-test="firstname"
              helperText={errors.firstname ? errors.firstname?.message : null}
            />
          )}
        />

        <FormSpacer type="input" />
        <Controller
          name="lastname"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              error={!!errors.lastname}
              label={'Last name'}
              id="lastname"
              data-track="lastname"
              data-track-value="mask"
              inputProps={{
                maxLength: 50,
                minLength: 1,
              }}
              data-test="lastname"
              helperText={errors.lastname ? errors.lastname?.message : null}
            />
          )}
        />

        <Dependents form={form} errorMessage={''} />

        <FormSpacer type="input" />
        <Controller
          rules={{ required: '' }}
          name="isLangInterpreter"
          defaultValue={isLangInterpreter}
          control={control}
          render={({ field }) => (
            <Toggle
              {...field}
              title={languageInterpreterTitle}
              required
              data-track="isLangInterpreter"
              data-track-value="capture"
              optionList={[
                {
                  autoComplete: 'off',
                  id: 'isLangInterpreter-yes',
                  label: 'Yes',
                  value: 'yes',
                  defaultChecked: 'yes' === isLangInterpreter,
                },
                {
                  autoComplete: 'off',
                  id: 'isLangInterpreter-no',
                  label: 'No',
                  value: 'no',
                  defaultChecked: 'no' === isLangInterpreter,
                },
              ]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target;
                setValue('isLangInterpreter', value);
                if (value === 'yes') {
                  setValue('vulnerableCustomer', 'yes');
                }
                triggerValidation('isLangInterpreter');
                field.onChange(value);
              }}
            />
          )}
        />

        {isLangInterpreter === 'yes' && (
          <>
            <FormGroupLabel title={languageTitle} alignCenter />
            <FormSpacer type="input" />
            <Dropdown
              sx={{ maxWidth: '400px' }}
              multiple={false}
              helperText=""
              label="Language"
              placeholder="Language"
              id="language"
              name="language"
              data-track="language"
              data-track-value="capture"
              value={watch('language') || ''}
              optionList={[
                'Arabic',
                'Cantonese',
                'Greek',
                'Hindi',
                'Italian',
                'Mandarin',
                'Punjabi',
                'Spanish',
                'Vietnamese',
                'Other',
              ].map((language) => ({
                id: language,
                label: language,
                value: language,
                defaultChecked: language === watch('language'),
              }))}
              onChange={(event) => {
                setValue('language', event.target.value as DeepPartial<string | null>);
                triggerValidation('language');
              }}
              onClose={() => triggerValidation('language')}
            />
          </>
        )}

        {isOtherLanguage && isLangInterpreter === 'yes' && (
          <>
            <FormSpacer type="input" />
            <FormGroupLabel title={otherLanguageTitle} alignCenter />
            <FormSpacer type="input" />
            <Controller
              name="otherLanguage"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value as string | undefined}
                  error={!!errors.otherLanguage}
                  label="Other language"
                  id="otherLanguage"
                  data-track="otherLanguage"
                  data-track-value="capture"
                  inputProps={{
                    maxLength: 50,
                    minLength: 3,
                  }}
                  data-test="otherLanguage"
                  helperText={errors.otherLanguage ? errors.otherLanguage?.message : null}
                />
              )}
            />
          </>
        )}

        {showNextButton && (
          <>
            <FormSpacer type="formgroup" />
            <Button type="button" label="Next" disabled={!isValid} onClick={handleClick} />
          </>
        )}
      </FormCard>
    </>
  );
};

export default PersonalDetails;
